import { Component, Input } from '@angular/core';

import { colourLabel } from '../../components/colour-select/colour-option';
import { BACKGROUND_OPTIONS, TEXT_COLOUR_OPTIONS } from './block-colours';
import type { EditableSection } from './content-sections-editor.component';
import { ALIGN_OPTIONS, BUTTON_ALIGN_OPTIONS, HEADING_LEVEL_OPTIONS, SPACING_OPTIONS } from './content-sections-options';

/**
 * A block's look (Robin, 2026-10-29): its colours, its heading's colour, level
 * and alignment, its buttons row's alignment, its spacing and the divider line
 * under it. Split out of ContentSectionsEditorComponent, which had passed 1,750
 * lines. It edits the section in place like the rest of the editor; the
 * editor's toMap serialises it.
 *
 * Which controls show is the editor's call:
 *   headingControls  the heading renders through the site's standard section
 *   blockControls    a standard block, not one that builds its own sections
 *                    (those ignore the buttons, spacing and divider settings)
 */
@Component({
  selector: 'app-block-look',
  templateUrl: './block-look.component.html',
})
export class BlockLookComponent {
  @Input() section!: EditableSection;
  @Input() headingControls = false;
  @Input() blockControls = false;

  readonly backgroundOptions = BACKGROUND_OPTIONS;
  readonly textColourOptions = TEXT_COLOUR_OPTIONS;
  readonly headingLevelOptions = HEADING_LEVEL_OPTIONS;
  readonly alignOptions = ALIGN_OPTIONS;
  readonly buttonAlignOptions = BUTTON_ALIGN_OPTIONS;
  readonly spacingOptions = SPACING_OPTIONS;

  /** Blank follows the Text colour, so say which. */
  get headingColourPlaceholder(): string {
    const text = this.section.textColour;
    return text ? `Same as text (${colourLabel(this.textColourOptions, text)})` : 'Same as text colour';
  }

  /** Blank buttons follow a heading set to Centre or Right (that one setting moved both when it shipped), else centre. */
  get buttonAlignPlaceholder(): string {
    const heading = this.section.align;
    if (heading === 'center') return 'Same as heading (Centre)';
    if (heading === 'right') return 'Same as heading (Right)';
    return 'Centre (default)';
  }

  /** On unless switched off; only "off" is stored. */
  get divider(): boolean {
    return this.section.divider !== false;
  }
  set divider(on: boolean) {
    this.section.divider = on ? undefined : false;
  }
}
